import { useRef, useState } from 'react'
import { IoChevronDown } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import CustomNavbar from '../../components/common/CustomNavbar';
import { whtsappIcon } from '../../asset';
import ECSLoader from '../../components/common/EcsLoader';
import { useOutsideClick } from '../../util/hooks/useDebounce';
import TemplateList from '../template/TemplateList';
import ExploreTemplateList from './ExploreTemplateList';
import CursorPagination from '../../components/common/CursorPagination';
import { getAuthUser } from "../../util/manageAuth";

const TABS = ['All', 'Approved', 'Pending', 'Draft', 'Rejected']
const TYPE_OPTIONS = ['All', 'Text', 'Image', 'Video', 'Document', 'Carousel']

export default function TemplateVariables() {
    const navigate = useNavigate();

    const dropdownRef = useRef<HTMLDivElement | null>(null);
    useOutsideClick(dropdownRef, () => setOpen(false));

    const [open, setOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('All')
    const [selectedTypes, setSelectedTypes] = useState<string[]>(['All'])
    const [isLoading, setIsLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pageInfo, setPageInfo] = useState<any>({});
    const [cursorMap, setCursorMap] = useState<Record<number, string>>({});

    const handlePageInfoChange = (info: any) => {
        setPageInfo(info);

        if (info?.after) {
            setCursorMap(prev => ({
                ...prev,
                [page + 1]: info.after,
            }));
        }
    };

    const handlePageSizeChange = (size: number) => {
        setPage(0);
        setPageSize(size);
        setCursorMap({});
    };

    const isExplore = activeTab === 'Explore';

    const role_admin = getAuthUser()?.role;
    const isSuperAdmin = role_admin?.toUpperCase() === "ROLE_ADMIN";
    const showCreateExample = isExplore && isSuperAdmin;

    // Cursors belong to one tab's result set — reset paging when switching tabs
    const handleTabChange = (tab: string) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        setPage(0);
        setPageInfo({});
        setCursorMap({});
    };

    const hasNext = !!pageInfo.after;

    const handleTypeToggle = (item: string) => {
        if (item === 'All') {
            setSelectedTypes(['All'])
            return
        }

        setSelectedTypes(prev => {
            const withoutAll = prev.filter(t => t !== 'All')
            if (withoutAll.includes(item)) {
                const updated = withoutAll.filter(t => t !== item)
                return updated.length === 0 ? ['All'] : updated
            } else {
                return [...withoutAll, item]
            }
        })
    }

    return (
        <PageLayout customNavbar={
            <CustomNavbar>
                <div className="flex gap-3 items-center w-max">
                    <img src={whtsappIcon} className="w-10 h-10" />
                    <span className="font-bold text-lg uppercase">Whatsapp Templates</span>
                </div>
            </CustomNavbar>
        }>
            <div className="flex gap-2 justify-end mb-3">

                <div ref={dropdownRef} className="relative">
                    <div className="p-[2px] rounded-4xl bg-brand-blue-gradient">
                        <button
                            onClick={() => setOpen(prev => !prev)}
                            className="flex items-center gap-2 px-5 py-2 rounded-4xl bg-white font-semibold text-text-secondary whitespace-nowrap"
                        >
                            {selectedTypes.includes('All') ? 'Filter by Type' : selectedTypes.join(', ')}
                            <IoChevronDown
                                className={`text-[16px] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                            />
                        </button>
                    </div>

                    {open && (
                        <div className="absolute mt-2 right-0 w-[160px] rounded-xl border border-[#E0E0E0] bg-white p-3 shadow-md z-20">
                            <div className="flex flex-col gap-2">
                                {TYPE_OPTIONS.map(item => {
                                    const isChecked = selectedTypes.includes(item)
                                    return (
                                        <label
                                            key={item}
                                            className="flex items-center gap-2 cursor-pointer"
                                            onClick={() => handleTypeToggle(item)}
                                        >
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isChecked ? 'bg-brand-main border-brand-main' : 'border-text-secondary'
                                                }`}>
                                                {isChecked && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                        <path d="M7.66667 1L3.08333 5.58333L1 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className={`text-sm font-semibold ${isChecked ? 'text-brand-main' : 'text-text-secondary'}`}>
                                                {item}
                                            </span>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* <button
                    onClick={() => navigate("/admin/variables-templates/add")}
                    className="flex items-center justify-center px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold whitespace-nowrap"
                >
                    + Create New Template
                </button> */}

                <button
                    onClick={() => navigate(showCreateExample
                        ? "/admin/variables-templates/add?mode=example"
                        : "/admin/variables-templates/add")}
                    className="flex items-center justify-center px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold whitespace-nowrap"
                >
                    {showCreateExample ? '+ Create Example Template' : '+ Create New Template'}
                </button>
            </div>

            <div className="rounded-4xl w-full bg-gray-100">
                <div className="flex flex-col rounded-4xl w-full p-4 bg-white min-h-screen relative overflow-hidden">
                    <ECSLoader show={isLoading} />

                    <div className="flex items-center justify-between gap-4 flex-wrap pb-3">
                        {/* Tabs */}
                        <div className="overflow-x-auto no-scrollbar max-w-full">
                            <div className="flex items-center gap-2">

                                {/* Explore */}
                                <button
                                    onClick={() => handleTabChange('Explore')}
                                    className={`px-5 py-2 rounded-full text-sm whitespace-nowrap font-semibold transition-all duration-200 border ${activeTab === 'Explore'
                                        ? 'bg-brand-main text-white border-brand-main shadow-sm'
                                        : 'bg-white text-text-secondary border-gray-200 hover:border-brand-main hover:text-brand-main'
                                        }`}
                                >
                                    Explore
                                </button>

                                {/* All + Status tabs */}
                                <div className="flex items-center gap-1 p-1 rounded-full bg-gray-100 border border-gray-200">
                                    {TABS.map(tab => {
                                        const isActive = activeTab === tab

                                        return (
                                            <button
                                                key={tab}
                                                onClick={() => handleTabChange(tab)}
                                                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${isActive
                                                    ? 'bg-white text-brand-main font-bold shadow-sm'
                                                    : 'text-text-secondary font-medium hover:text-brand-main'
                                                    }`}
                                            >
                                                {tab}
                                            </button>
                                        )
                                    })}
                                </div>

                            </div>
                        </div>

                        <CursorPagination
                            page={page}
                            setPage={setPage}
                            totalItems={pageInfo.total || 0}
                            pageSize={pageSize}
                            setPageSize={handlePageSizeChange}
                            hasNext={hasNext}
                            loading={isLoading}
                        />
                    </div>

                    <div className='flex flex-wrap gap-x-5 gap-y-5 sm:mt-6 mt-4'>
                        {isExplore ? (
                            <>
                                {isSuperAdmin && (
                                    <div
                                        onClick={() => navigate("/admin/variables-templates/add?mode=example")}
                                        className="flex p-7 justify-center items-center w-full sm:max-w-[280px] border-2 border-dashed border-gray-300 px-8 rounded-tl-4xl rounded-br-4xl rounded-2xl hover:border-brand-main transition-all duration-200 cursor-pointer"
                                    >
                                        <div className="flex flex-col items-center text-center gap-4">
                                            <div className="w-10 h-10 rounded-full border-2 border-text-secondary flex items-center justify-center text-text-secondary text-2xl font-bold leading-none">
                                                +
                                            </div>
                                            <p className="font-bold text-lg text-text-secondary">
                                                Create an example template
                                            </p>
                                            <p className="text-xs text-text-secondary max-w-[200px]">
                                                Add a ready-made template to the library so users can start from it.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <ExploreTemplateList
                                    selectedTypes={selectedTypes}
                                    onLoadingChange={setIsLoading}
                                    page={page}
                                    pageSize={pageSize}
                                    cursor={cursorMap[page]}
                                    onPageInfoChange={handlePageInfoChange}
                                />
                            </>
                        ) : (
                            <>
                                <div
                                    onClick={() => navigate("/admin/variables-templates/add")}
                                    className="flex p-7 justify-center items-center w-full sm:max-w-[280px] border-2 border-dashed border-gray-300 px-8 rounded-tl-4xl rounded-br-4xl rounded-2xl hover:border-brand-main transition-all duration-200 cursor-pointer"
                                >
                                    <div className="flex flex-col items-center text-center gap-4">
                                        <div className="w-10 h-10 rounded-full border-2 border-text-secondary flex items-center justify-center text-text-secondary text-2xl font-bold leading-none">
                                            +
                                        </div>
                                        <p className="font-bold text-lg text-text-secondary">
                                            Create your next template
                                        </p>
                                        <p className="text-xs text-text-secondary max-w-[200px]">
                                            Start by creating a WhatsApp message template to connect with your customers instantly.
                                        </p>
                                    </div>
                                </div>

                                <TemplateList
                                    activeTab={activeTab}
                                    selectedTypes={selectedTypes}
                                    onLoadingChange={setIsLoading}
                                    page={page}
                                    pageSize={pageSize}
                                    cursor={cursorMap[page]}
                                    onPageInfoChange={handlePageInfoChange}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </PageLayout>
    )
}
